import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Function to save Google avatar as agent avatar
  const saveGoogleAvatarAsAgentAvatar = async (googlePictureUrl) => {
    try {
      console.log('Auto-saving Google avatar as agent avatar:', googlePictureUrl);
      
      // Check if agent already has an avatar
      const agentResponse = await fetch('/api/agents/settings', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (agentResponse.ok) {
        const agentData = await agentResponse.json();
        // Only save if no agent avatar exists
        if (!agentData.agent_avatar || agentData.agent_avatar.includes('googleusercontent.com')) {
          // Convert Google image to data URL
          const response = await fetch(googlePictureUrl);
          const blob = await response.blob();
          
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = async (e) => {
              const dataURL = e.target.result;
              
              try {
                const saveResponse = await fetch(`/api/agents/settings`, {
                  method: 'PUT',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({
                    ...agentData,
                    agent_avatar: dataURL
                  })
                });
                
                if (saveResponse.ok) {
                  console.log('Google avatar auto-saved as agent avatar');
                  resolve();
                } else {
                  console.log('Failed to auto-save Google avatar');
                  resolve(); // Don't reject, just log and continue
                }
              } catch (error) {
                console.log('Error auto-saving Google avatar:', error);
                resolve(); // Don't reject, just log and continue
              }
            };
            reader.onerror = () => resolve(); // Don't reject, just continue
            reader.readAsDataURL(blob);
          });
        } else {
          console.log('Agent already has a custom avatar, skipping auto-save');
        }
      }
    } catch (error) {
      console.log('Error checking agent settings for auto-save:', error);
    }
  };

  // Check for token in URL parameters (OAuth callback)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('token');
    const errorFromUrl = urlParams.get('error');
    
    console.log('AuthContext: Checking URL parameters');
    console.log('Token from URL:', tokenFromUrl ? 'found' : 'not found');
    console.log('Error from URL:', errorFromUrl || 'none');
    
    if (tokenFromUrl) {
      console.log('Token found in URL, storing in localStorage');
      localStorage.setItem('token', tokenFromUrl);
      setToken(tokenFromUrl);
      
      // Clean up URL by removing token parameter
      const newUrl = window.location.pathname;
      console.log('Cleaning up URL, redirecting to:', newUrl);
      window.history.replaceState({}, document.title, newUrl);
    } else if (errorFromUrl) {
      console.error('OAuth error:', errorFromUrl);
      // Clean up URL by removing error parameter
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      console.log('AuthContext: checkAuth called, token exists:', !!token);
      
      if (token) {
        try {
          console.log('AuthContext: Making request to /auth/me');
          const response = await fetch('/auth/me', {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          console.log('AuthContext: /auth/me response status:', response.status);
          
          if (response.ok) {
            const data = await response.json();
            console.log('=== AUTH CONTEXT: User data received ===');
            console.log('AuthContext received user data:', data);
            console.log('data.user:', data.user);
            console.log('data.user.early_access_approved:', data.user?.early_access_approved);
            console.log('typeof data.user.early_access_approved:', typeof data.user?.early_access_approved);
            
            // Validate that we have a real user with an ID
            if (data.user && data.user.id && data.user.email) {
              console.log('✅ AuthContext: Valid user data, setting user');
              setUser(data.user);
              
              // Auto-save Google avatar on first login or if no agent avatar exists
              if (data.user?.picture) {
                await saveGoogleAvatarAsAgentAvatar(data.user.picture);
              }
            } else {
              // Invalid user data, clear everything
              console.log('❌ Invalid user data received, clearing auth');
              localStorage.clear();
              sessionStorage.clear();
              setToken(null);
              setUser(null);
            }
          } else {
            // Token is invalid, remove it
            console.log('Auth check failed with status:', response.status);
            localStorage.clear();
            sessionStorage.clear();
            setToken(null);
            setUser(null);
          }
        } catch (error) {
          console.error('Auth check failed:', error);
          localStorage.clear();
          sessionStorage.clear();
          setToken(null);
          setUser(null);
        }
      } else {
        console.log('AuthContext: No token, setting loading to false');
      }
      setLoading(false);
    };

    checkAuth();
  }, [token]);


  const login = () => {
    // In development, redirect to server port; in production, use relative URL
    const isDevelopment = window.location.hostname === 'localhost';
    const oauthUrl = isDevelopment ? 'https://localhost:5001/auth/google' : '/auth/google';
    window.location.href = oauthUrl;
  };

  const logout = async () => {
    try {
      await fetch('/auth/logout', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear all possible cached data
      localStorage.clear();
      sessionStorage.clear();
      setToken(null);
      setUser(null);
      
      // Force a page reload to clear any other cached state
      window.location.reload();
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
